import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Flame, DollarSign, MapPin } from "lucide-react";

export default function DriverHeatMap() {
  const [city, setCity] = useState("");
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/gigs/heatmap?city=${city}&days=${days}`).then((r) => setD(r.data)).catch(() => {}); }, [city, days]);
  if (!d) return <p className="text-slate-500 text-sm p-6">Loading heat map…</p>;
  const max = Math.max(1, ...d.cells.map((c) => c.jobs));
  const maxPay = Math.max(1, ...d.cells.map((c) => c.avg_pay));
  const bounds = d.cells.length ? L.latLngBounds(d.cells.map((c) => [c.lat, c.lng])).pad(0.4) : null;
  const color = (p) => { const t = Math.min(1, p / maxPay); return `hsl(${Math.round(40 + 120 * t)}, 90%, 55%)`; };
  return (
    <div className="space-y-4" data-testid="driver-heatmap">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-slate-300 flex items-center gap-2"><Flame className="w-4 h-4 text-amber-400" />{d.total_jobs} jobs in the last {d.days} days</p>
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Filter by city" data-testid="heat-city-filter" className="bg-slate-900 border-slate-800 w-40 h-9" />
        <div className="flex gap-1.5">{[7, 14, 30].map((n) => <button key={n} onClick={() => setDays(n)} data-testid={`heat-days-${n}`} className={`px-2.5 py-1 rounded-full text-xs border ${days === n ? "bg-amber-500/20 border-amber-500/40 text-amber-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`}>{n}d</button>)}</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="rounded-2xl overflow-hidden border border-slate-800" data-testid="heat-map">
          {bounds ? (
            <MapContainer key={d.cells.length + city + days} bounds={bounds} style={{ height: 420, width: "100%", background: "#0B0D12" }} attributionControl={false}>
              <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" className="map-dark-tiles" />
              {d.cells.map((c, i) => (
                <CircleMarker key={`${c.lat},${c.lng}`} center={[c.lat, c.lng]} radius={10 + 30 * (c.jobs / max)} pathOptions={{ color: color(c.avg_pay), fillColor: color(c.avg_pay), fillOpacity: 0.35, weight: 1.5 }}>
                  <Tooltip direction="top" className="!bg-[#0B0D12] !border-slate-700 !text-slate-200 !text-xs"><b>{c.jobs} jobs</b> · avg ${c.avg_pay} · {c.open} open now</Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          ) : <div className="h-[420px] flex items-center justify-center text-slate-500 text-sm" data-testid="heat-empty">No geocoded jobs in this window yet.</div>}
        </div>
        <aside className="space-y-3">
          {d.best_pay_cell && <div className="p-4 rounded-2xl bg-[#131722] border border-emerald-500/30" data-testid="heat-best">
            <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 flex items-center gap-1"><DollarSign className="w-3 h-3" />Best paying area</p>
            <p className="font-display font-extrabold text-2xl mt-0.5">${d.best_pay_cell.avg_pay} <span className="text-xs text-slate-500 font-sans font-normal">avg driver pay</span></p>
            <p className="text-xs text-slate-400">{d.best_pay_cell.city} · {d.best_pay_cell.jobs} jobs · {d.best_pay_cell.open} open</p>
          </div>}
          <div className="p-4 rounded-2xl bg-[#131722] border border-slate-800" data-testid="heat-cities">
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" />Hottest cities</p>
            <ol className="space-y-1.5">{d.cities.map((c, i) => (
              <li key={c.city} className="text-sm flex items-center gap-2" data-testid={`heat-city-${i}`}>
                <span className="font-mono text-slate-500 w-4">{i + 1}</span><span className="flex-1 truncate">{c.city}</span>
                <span className="text-xs text-slate-400">{c.jobs} jobs</span><span className="text-xs font-mono text-emerald-300">${c.avg_pay}</span>
              </li>
            ))}{d.cities.length === 0 && <li className="text-xs text-slate-500">No data yet.</li>}</ol>
          </div>
          <p className="text-[11px] text-slate-500">Circle size = job volume · color = average driver pay (amber → green). Position yourself in hot zones before the 15-minute priority window opens.</p>
        </aside>
      </div>
    </div>
  );
}
